import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import api from '../../services/axiosClient';
import tenantService from '../../services/tenant.service';
import { isSuperAdminRole, isTenantAdminRole } from '../../utils/userRoles';
import { canAccessDialplanBot, canAccessDialplanOverview } from '../../utils/dialplanAccess';
import { getHiddenPageIds } from '../../utils/pageAccess';
import { 
  FiGrid,
  FiBarChart2,
  FiTool, 
  FiDatabase, 
  FiMessageSquare, 
  FiUser, 
  FiMic, 
  FiPhone, 
  FiPhoneCall, 
  FiCheckSquare, 
  FiLifeBuoy,
  FiCalendar,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiZap,
  FiShuffle,
  FiKey,
  FiAlertCircle,
  FiMail,
  FiChevronDown,
  FiChevronRight
} from 'react-icons/fi';
import { FaClock } from "react-icons/fa";
import { RiVoiceAiLine } from 'react-icons/ri';
import { BiSolidUserVoice } from "react-icons/bi";
import { FaBuildingUser } from "react-icons/fa6";
import { TbSettingsSpark } from "react-icons/tb";
import { MdCrisisAlert } from "react-icons/md";
import { Wand2 } from "lucide-react";

interface MenuItem {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  path: string;
  requiresAuth?: boolean;
  badgeCount?: number;
}

interface MenuSection {
  id: string;
  labelKey: string;
  itemIds: string[];
  groups?: {
    id: string;
    labelKey: string;
    itemIds: string[];
  }[];
}

const MENU_SECTIONS: MenuSection[] = [
  {
    id: 'ai-setup',
    labelKey: 'sidebarCategoryAiSetup',
    itemIds: [
      'agents',
      'default-agent',
      'custom-tools',
      'knowledge-base',
      'dialplan-bot',
      'systemprompt-generator',
      'voices',
    ],
    groups: [
      {
        id: 'ai-setup-agents',
        labelKey: 'sidebarCategoryAiSetupAgents',
        itemIds: ['agents', 'default-agent', 'voices'],
      },
      {
        id: 'ai-setup-knowledge',
        labelKey: 'sidebarCategoryAiSetupKnowledge',
        itemIds: ['knowledge-base', 'custom-tools'],
      },
      {
        id: 'ai-setup-automation',
        labelKey: 'sidebarCategoryAiSetupAutomation',
        itemIds: ['dialplan-bot', 'dialplan-overview', 'systemprompt-generator'],
      },
    ],
  },
  {
    id: 'operations',
    labelKey: 'sidebarCategoryOperations',
    itemIds: [
      'conversations',
      'speech-to-text',
      'todos',
      'calendar',
      'opening-hours',
      'redirect-numbers',
    ],
    groups: [
      {
        id: 'operations-communication',
        labelKey: 'sidebarCategoryOperationsCommunication',
        itemIds: ['conversations', 'speech-to-text', 'todos'],
      },
      {
        id: 'operations-organization',
        labelKey: 'sidebarCategoryOperationsOrganization',
        itemIds: ['calendar', 'opening-hours', 'redirect-numbers'],
      },
    ],
  },
  {
    id: 'outbound',
    labelKey: 'sidebarCategoryOutbound',
    itemIds: ['contacts', 'outbound', 'autocall'],
    groups: [
      {
        id: 'outbound-campaigns',
        labelKey: 'sidebarCategoryOutboundCampaigns',
        itemIds: ['outbound', 'autocall'],
      },
      {
        id: 'outbound-data',
        labelKey: 'sidebarCategoryOutboundData',
        itemIds: ['contacts'],
      },
    ],
  },
  {
    id: 'admin',
    labelKey: 'sidebarCategoryAdmin',
    itemIds: ['tenants', 'users', 'tickets', 'feedbacks', 'monitoring', 'system-mail-logs', 'tokens', 'twilio-numbers'],
    groups: [
      {
        id: 'admin-people',
        labelKey: 'sidebarCategoryAdminPeople',
        itemIds: ['tenants', 'users'],
      },
      {
        id: 'admin-support',
        labelKey: 'sidebarCategoryAdminSupport',
        itemIds: ['tickets', 'feedbacks'],
      },
      {
        id: 'admin-monitoring',
        labelKey: 'sidebarCategoryAdminMonitoring',
        itemIds: ['monitoring', 'system-mail-logs'],
      },
      {
        id: 'admin-integrations',
        labelKey: 'sidebarCategoryAdminIntegrations',
        itemIds: ['tokens', 'twilio-numbers'],
      },
    ],
  },
];

type BackendBuildInfo = {
  version?: string;
  buildCommit?: string;
  buildRef?: string;
  buildTime?: string;
  deploymentSlot?: string;
};

const shortBuildCommit = (value: string | null | undefined): string => {
  if (!value || value === 'local') return value || '-';
  return value.slice(0, 7);
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [backendBuildInfo, setBackendBuildInfo] = useState<BackendBuildInfo | null>(null);
  const [monitoringIncidentCount, setMonitoringIncidentCount] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(MENU_SECTIONS.map(section => [section.id, true]))
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      MENU_SECTIONS
        .flatMap(section => (section.groups || []).map(group => [`${section.id}:${group.id}`, true] as const))
    )
  );
  const { user } = useAuth();
  const isSuperAdmin = isSuperAdminRole(user?.role);
  const canManageUsers = isSuperAdmin || isTenantAdminRole(user?.role);

  // Environment variables with defaults
  const logoUrl = user?.tenant?.logoUrl_small || '/vite.svg';
  const brandColor = user?.tenant?.primaryColor || '#3d3b8e';
  const contactEmail = import.meta.env.VITE_CONTACT_EMAIL || 'support@agenturphilipp.de';

  const hiddenPages = getHiddenPageIds(user);
  const showDialplanBot = canAccessDialplanBot(user?.role, user?.tenant);
  const showDialplanOverview =
    canAccessDialplanOverview(user?.role) && !hiddenPages.has('dialplan-overview');

  useEffect(() => {
    if (!isSuperAdmin) {
      setBackendBuildInfo(null);
      return;
    }

    let isMounted = true;

    const loadBackendVersion = async () => {
      try {
        const response = await api.get<BackendBuildInfo>('/utils/version/admin');
        if (isMounted) {
          setBackendBuildInfo(response.data);
        }
      } catch {
        if (isMounted) {
          setBackendBuildInfo({ version: '-' });
        }
      }
    };

    void loadBackendVersion();

    return () => {
      isMounted = false;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setMonitoringIncidentCount(0);
      return;
    }

    let isMounted = true;

    const loadMonitoringSummary = async () => {
      try {
        const summary = await tenantService.getPostCallMonitoringSummary();
        if (isMounted) {
          setMonitoringIncidentCount(summary.activeCount);
        }
      } catch {
        if (isMounted) {
          setMonitoringIncidentCount(0);
        }
      }
    };

    const triggerLoad = () => {
      if (document.visibilityState !== 'hidden') {
        void loadMonitoringSummary();
      }
    };

    triggerLoad();
    const intervalId = window.setInterval(triggerLoad, 30000);
    document.addEventListener('visibilitychange', triggerLoad);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', triggerLoad);
    };
  }, [isSuperAdmin]);

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      labelKey: 'dashboard',
      icon: <FiGrid className="w-5 h-5" />,
      path: '/',
    },
    {
      id: 'agents',
      labelKey: 'agents',
      icon: <RiVoiceAiLine className="w-5 h-5" />,
      path: '/agents',
    },
    {
      id: 'default-agent',
      labelKey: 'systemPromptPage',
      icon: <TbSettingsSpark className="w-5 h-5" />,
      path: '/systemprompt',
    },
    {
      id: 'custom-tools',
      labelKey: 'customTools',
      icon: <FiTool className="w-5 h-5" />,
      path: '/custom-tools',
    },
    {
      id: 'knowledge-base',
      labelKey: 'knowledgeBase',
      icon: <FiDatabase className="w-5 h-5" />,
      path: '/knowledgebases',
    },
    ...(showDialplanBot ? [{
      id: 'dialplan-bot',
      labelKey: 'dialplanBot',
      icon: <Wand2 className="w-5 h-5" />,
      path: '/dialplan-bot',
    }] : []),
    ...(showDialplanOverview ? [{
      id: 'dialplan-overview',
      labelKey: 'dialplanOverview',
      icon: <FiBarChart2 className="w-5 h-5" />,
      path: '/dialplan-overview',
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'twilio-numbers',
      labelKey: 'twilioNumbers',
      icon: <FiAlertCircle className="w-5 h-5" />,
      path: '/twilio-numbers',
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'systemprompt-generator',
      labelKey: 'systemPromptGenerator',
      icon: <FiZap className="w-5 h-5" />,
      path: '/systemprompt-generator',
    }] : []),
    {
      id: 'conversations',
      labelKey: 'conversations',
      icon: <FiMessageSquare className="w-5 h-5" />,
      path: '/conversations',
    },
    {
      id: 'contacts',
      labelKey: 'contacts',
      icon: <FiUser className="w-5 h-5" />,
      path: '/contacts',
    },
    {
      id: 'voices',
      labelKey: 'voices',
      icon: <FiMic className="w-5 h-5" />,
      path: '/voices',
    },
    {
      id: 'speech-to-text',
      labelKey: 'transcribe',
      icon: <BiSolidUserVoice className="w-5 h-5" />,
      path: '/speech-to-text',
    },
    {
      id: 'outbound',
      labelKey: 'outbound',
      icon: <FiPhone className="w-5 h-5" />,
      path: '/outbound-calls',
    },
    {
      id: 'autocall',
      labelKey: 'autocall',
      icon: <FiPhoneCall className="w-5 h-5" />,
      path: '/autocall',
    },
    {
      id: 'todos',
      labelKey: 'todos',
      icon: <FiCheckSquare className="w-5 h-5" />,
      path: '/todos',
    },
    {
      id: 'tickets',
      labelKey: isSuperAdmin ? 'ticketsFeatureList' : 'tickets',
      icon: <FiLifeBuoy className="w-5 h-5" />,
      path: '/tickets',
    },
    {
      id: 'calendar',
      labelKey: 'calendar',
      icon: <FiCalendar className="w-5 h-5" />,
      path: '/calendar',
    },
    ...(canManageUsers ? [{
      id: 'users',
      labelKey: 'users',
      icon: <FiUser className="w-5 h-5" />,
      path: '/users',
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'tenants',
      labelKey: 'tenants',
      icon: <FaBuildingUser className="w-5 h-5" />,
      path: '/tenants',
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'feedbacks',
      labelKey: 'feedbacks',
      icon: <FiMessageSquare className="w-5 h-5" />,
      path: '/feedbacks',
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'monitoring',
      labelKey: 'monitoring',
      icon: <MdCrisisAlert className="w-5 h-5" />,
      path: '/monitoring',
      badgeCount: monitoringIncidentCount,
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'tokens',
      labelKey: 'tokens',
      icon: <FiKey className="w-5 h-5" />,
      path: '/tokens',
    }] : []),
    ...(user?.role === 'SuperAdmin' ? [{
      id: 'system-mail-logs',
      labelKey: 'systemMailLogsMenu',
      icon: <FiMail className="w-5 h-5" />,
      path: '/system-mail-logs',
    }] : []),
    {
      id: 'opening-hours',
      labelKey: 'opening-hours',
      icon: <FaClock className="w-5 h-5" />,
      path: '/opening-hours',
    },
    {
      id: 'redirect-numbers',
      labelKey: 'redirectNumbers',
      icon: <FiShuffle className="w-5 h-5" />,
      path: '/redirect-numbers',
    },
    {
      id: 'profile',
      labelKey: 'profile',
      icon: <FiSettings className="w-5 h-5" />,
      path: '/profile',
    },
  ];

  const visibleMenuItems = menuItems.filter(item => !hiddenPages.has(item.id));
  const menuItemMap = new Map(visibleMenuItems.map(item => [item.id, item]));
  const dashboardItem = menuItemMap.get('dashboard');
  const groupedItemIds = new Set<string>();
  const visibleSections = MENU_SECTIONS
    .map(section => {
      const items = section.itemIds
        .map(itemId => menuItemMap.get(itemId))
        .filter((item): item is MenuItem => Boolean(item));

      if (items.length >= 2) {
        items.forEach(item => groupedItemIds.add(item.id));
      }

      return {
        ...section,
        items,
        isVisible: items.length >= 2,
      };
    })
    .filter(section => section.isVisible);
  const standaloneItems = visibleMenuItems.filter(
    item => item.id !== 'dashboard' && !groupedItemIds.has(item.id)
  );

  useEffect(() => {
    setExpandedSections(current => {
      const next = { ...current };
      for (const section of MENU_SECTIONS) {
        if (!(section.id in next)) {
          next[section.id] = true;
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedGroups(current => {
      const next = { ...current };
      for (const section of MENU_SECTIONS) {
        for (const group of section.groups || []) {
          const key = `${section.id}:${group.id}`;
          if (!(key in next)) {
            next[key] = true;
          }
        }
      }
      return next;
    });
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(current => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  const toggleGroup = (sectionId: string, groupId: string) => {
    const key = `${sectionId}:${groupId}`;
    setExpandedGroups(current => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item: MenuItem) => (
    <button
      key={item.id}
      onClick={() => handleNavigation(item.path)}
      className={`
        w-full flex items-center space-x-3 px-3 py-3 rounded-lg
        text-left transition-all duration-200 ease-in-out
        ${
          isActiveRoute(item.path)
            ? `bg-white/20 text-white font-medium`
            : `text-white/80 hover:bg-white/10 hover:text-white`
        }
      `}
    >
      {item.icon}
      <span className="text-sm font-medium flex-1 min-w-0">
        {t(item.labelKey)}
      </span>
      {typeof item.badgeCount === 'number' && item.badgeCount > 0 && (
        <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm">
          {item.badgeCount > 99 ? '99+' : item.badgeCount}
        </span>
      )}
    </button>
  );

  const renderSection = (section: (typeof visibleSections)[number]) => {
    const isExpanded = expandedSections[section.id] ?? true;

    return (
      <div
        key={section.id}
        className="rounded-xl border border-white/10 bg-white/5 px-3 py-3"
      >
        <button
          type="button"
          onClick={() => toggleSection(section.id)}
          className="flex w-full items-center justify-between px-3 pb-2 text-left text-[11px] font-medium uppercase tracking-[0.14em] text-white/55 transition-colors hover:text-white/80"
          aria-expanded={isExpanded}
        >
          <span>{t(section.labelKey)}</span>
          {isExpanded ? <FiChevronDown className="h-4 w-4" /> : <FiChevronRight className="h-4 w-4" />}
        </button>
        {isExpanded && (
          section.groups ? (
            <div className="space-y-3">
              {section.groups.map((group) => {
                const groupItems = group.itemIds
                  .map((itemId) => section.items.find((item) => item.id === itemId))
                  .filter((item): item is MenuItem => Boolean(item));
                const groupKey = `${section.id}:${group.id}`;
                const isGroupExpanded = expandedGroups[groupKey] ?? true;

                if (groupItems.length === 0) return null;

                return (
                  <div key={group.id} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => toggleGroup(section.id, group.id)}
                      className="flex w-full items-center justify-between px-3 pt-1 text-left text-[10px] font-medium uppercase tracking-[0.14em] text-white/50 transition-colors hover:text-white/80"
                      aria-expanded={isGroupExpanded}
                    >
                      <span>{t(group.labelKey)}</span>
                      {isGroupExpanded ? <FiChevronDown className="h-3.5 w-3.5" /> : <FiChevronRight className="h-3.5 w-3.5" />}
                    </button>
                    {isGroupExpanded && groupItems.map(renderMenuItem)}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1">
              {section.items.map(renderMenuItem)}
            </div>
          )
        )}
      </div>
    );
  };

  // Dynamic styles based on dark mode
  const sidebarBg = brandColor;
  const borderColor = 'rgba(255, 255, 255, 0.1)';

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md text-white"
        style={{ backgroundColor: brandColor }}
        onClick={toggleMobileMenu}
      >
        {isMobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
      </button>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-50 w-72 
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col h-screen lg:h-screen
        `}
        style={{ backgroundColor: sidebarBg }}
      >
        {/* Logo Section */}
        <button
          type="button"
          onClick={() => handleNavigation('/')}
          className="flex cursor-pointer items-center justify-center p-6 transition-opacity hover:opacity-90"
          aria-label={t('dashboard')}
        >
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-12 w-auto"
            onError={(e) => {
              // Fallback to text if image fails to load
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <div className="hidden text-white text-2xl font-bold">
            PHILIPP
          </div>
        </button>

        {/* Menu Label */}
        <div className="px-6 py-4">
          <h2 className={`text-sm font-medium uppercase tracking-wide ${
            'text-white'
          } opacity-70`}>
            {t('menu')}
          </h2>
        </div>

        {/* Menu Items - Scrollable */}
        <nav className="flex-1 overflow-y-auto px-4 space-y-3">
          {dashboardItem && (
            <div className="space-y-1">
              {renderMenuItem(dashboardItem)}
            </div>
          )}

          {visibleSections.map(renderSection)}

          {standaloneItems.length > 0 && (
            <div className="space-y-1">
              {standaloneItems.map(renderMenuItem)}
            </div>
          )}
        </nav>

        {/* Logout Button */}
        <div 
          className="px-4 py-4"
          style={{ borderTop: `1px solid ${borderColor}` }}
        >
          <button
            onClick={handleLogout}
            className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg
                     transition-all duration-200 ease-in-out
                     text-white/80 hover:bg-red-500/20 hover:text-red-200`}
          >
            <FiLogOut className="w-5 h-5" />
            <span className="text-sm font-medium">{t('logout')}</span>
          </button>
        </div>

        {/* Contact Info */}
        <div 
          className="px-6 py-4"
          style={{ borderTop: `1px solid ${borderColor}` }}
        >
          <div className="text-center">
            <p className={`text-xs mb-1 ${
              'text-white/60'
            }`}>
              {t('helpAndSupport')}
            </p>
            <a 
              href={`mailto:${contactEmail}`}
              className={`text-xs transition-colors ${
                'text-white/80 hover:text-white'
              }`}
            >
              {contactEmail}
            </a>
            {isSuperAdmin && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-white/65">
                <span title={`ref=${__APP_BUILD_REF__} build=${__APP_BUILD_TIME__}`}>
                  FE v{__APP_VERSION__} ({shortBuildCommit(__APP_BUILD_COMMIT__)})
                </span>
                <span className="text-white/35">|</span>
                <span title={`ref=${backendBuildInfo?.buildRef || '-'} build=${backendBuildInfo?.buildTime || '-'}`}>
                  BE v{backendBuildInfo?.version ?? '...'} ({shortBuildCommit(backendBuildInfo?.buildCommit)})
                </span>
                <span className="text-white/35">|</span>
                <span>
                  Slot {backendBuildInfo?.deploymentSlot ?? '...'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
